import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/standalone-services-page.module.css'
// eslint-disable-next-line import/extensions
import destruction from '../../json/certified-destruction-data.json'

const destructionServiceData = {
  name: 'Certified Destruction Services',
  description:
    'Secure, documented destruction of confidential records, data-bearing media, and physical product nationwide. Dimension provides on-site and off-site destruction with a Certificate of Destruction and recycling of destruction residue.',
  serviceType: 'Certified Destruction',
  url: 'https://getdimension.com/certified-destruction',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Document and Records Destruction' },
    { name: 'Hard Drive and Electronic Media Destruction' },
    { name: 'Product and Inventory Destruction' },
    { name: 'On-Site Destruction' },
  ],
}

const destructionFAQData = {
  questions: destruction.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' ').replace(/<[^>]*>/g, ''),
  })),
}

function CertifiedDestruction() {
  return (
    <>
      <Head>
        <title>Certified Destruction Services | Dimension</title>
        <meta
          name="description"
          content="Secure certified destruction of confidential documents, hard drives, and product nationwide. On-site or off-site destruction with a Certificate of Destruction on every order."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/certified-destruction" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={destructionServiceData} />
        <SchemaMarkup type="FAQPage" data={destructionFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={destruction.pitch.heading}
          material={destruction.pitch.material}
          pitchText={destruction.pitch.pitchText}
          ctaLink={destruction.pitch.ctaLink}
          ctaText={destruction.pitch.ctaText}
          ctaHeading="Need something destroyed, not just hauled away?"
          ctaSubtext="Tell us what needs destroying and our team returns a written, no-obligation quote. Every certified destruction order is scoped and priced by a person."
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...destruction.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...destruction.benefits} />
        <Approach {...destruction.approach} />
        <FAQ {...destruction.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'E-Waste and ITAD',
              href: '/ewaste',
              description:
                'Enterprise electronics recycling with data destruction, value recovery, and R2-certified processing.',
            },
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Compliant collection, transport, and disposal of hazardous materials with full manifest documentation.',
            },
            {
              label: 'B2B Waste Solutions',
              href: '/b2b-solutions',
              description:
                'Multi-site waste and recycling programs run through one account, one contact, and one set of reporting.',
            },
            {
              label: 'Sustainability Reporting',
              href: '/sustainability',
              description:
                'Diversion data and reporting that turn disposal activity into numbers your ESG team can use.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default CertifiedDestruction
